import * as React from 'react'
import {Box, Typography, TextField, InputAdornment, MenuItem, Tabs, Tab, Button} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import {useSnackbar} from 'notistack'
import {sectionTitleSx, sectionCardSx, filterBarSx, alertStripSx, primaryBtnSx, secondaryBtnSx} from '../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {SUBTLE, INFO, PRIMARY_BLUE} from '../../../styles/foundation/colors'
import {GAP} from '../../../styles/foundation/spacing'
import {MERCHANT} from './data/noberoFreeze'
import {FREEZE_LEVEL, FREEZE_LEVEL_LABEL} from './data/reference'
import {requestCounts, filterRequests, applySubmission, actorFor, todayISO, MAX_SUBMISSIONS} from './requests/logic'
import {NO_FREEZE_BODY, NO_FREEZE_NOTE_BOLD, NO_FREEZE_NOTE} from './requests/copy'
import EmptyState from './components/EmptyState'
import RqStats from './requests/RqStats'
import RqApproved from './requests/RqApproved'
import RqReview from './requests/RqReview'
import RqRejected from './requests/RqRejected'

// Type/density parity with disputes-v2 (see sdd/type-parity-spec.md): the level
// filter's closed control already renders at SIZE['2xs'], so its portalled menu
// needs the same override or it opens at the browser default 16px.
const selectMenuProps = {PaperProps: {sx: {'& .MuiMenuItem-root': {fontSize: SIZE['2xs']}}}}

// Every keystroke would otherwise re-filter the whole collection and
// re-render three tabs' worth of cards/rows — the same deliberate deviation
// from the (unde-bounced) mockup that initiate/IniFilterBar.js already makes.
const DEBOUNCE_MS = 200

// The three status labels, defined once. FreezeDashboard drives this axis from
// its own four-tab row (see its header), so without one exported definition
// these strings would exist as two typed copies free to drift apart. Exported
// raw as well as via `statusTabLabel` below, because that row carries its count
// as a separate field on the tab rather than baked into the label string.
export const STATUS_LABEL = {
  approved: 'Approved weight freezes',
  review: 'Under review',
  rejected: 'Rejected',
}

/**
 * One status tab's label, count included. `rows` is a `filterRequests()`
 * partition, so the count describes whatever collection the CALLER filtered —
 * the search box below for this component's own row, the whole collection for
 * the dashboard's row above it.
 */
export function statusTabLabel(id, rows) {
  return `${STATUS_LABEL[id]} (${rows[id].length})`
}

// Left-to-right order of this component's OWN row only. The dashboard orders
// its four-tab row itself and does not read this.
const STATUS_ORDER = ['approved', 'review', 'rejected']

/**
 * Weight Freeze (v2) — Requests: the section shell (Section 5 of 6).
 *
 * Every weight freeze request on this merchant, in one of three states:
 * approved (with the preliminary/in-force distinction), under review (the
 * merchant's only in-flight action — a submission against a 3-slot budget),
 * or rejected (final; the only route forward is a fresh, blank request).
 *
 * `requests`/`packs`/`actions` come from the ONE shared store instantiated
 * once in index.js (`useFreezeStore`) and threaded down — never from the
 * frozen `data/noberoFreeze.js` import directly and never from a
 * section-local `useState`. index.js unmounts whichever section isn't
 * active on every rail click; a second, local copy of `REQUESTS` here would
 * mean a request Initiate just raised (via the SAME store's `addRequest`)
 * would never show up on this page. The only mutation this section performs
 * — a submission against an existing request — goes through the store's own
 * `actions.submitAdditionalData`, which delegates to `requests/logic.js`'s
 * `applySubmission` (see state/useFreezeStore.js's own SUBMIT_ADDITIONAL_DATA
 * case) so the round-advance/3-submission-cap/history-stamping rules exist
 * in exactly one place.
 *
 * NOT PERSISTED. Nothing here writes back to data/noberoFreeze.js and there
 * is no backend; a page reload discards every submission, exactly like
 * every other mutation in this module (see useFreezeStore.js's own header).
 *
 * The status axis is CONTROLLED-OR-UNCONTROLLED, the same shape index.js uses
 * for the module axis: supply `tab` and the caller owns which status is on
 * screen and renders the only tab row; omit it and this component keeps its
 * own state and its own row, unchanged. No `onTabChange` counterpart, because
 * nothing in here ever moves the status axis — the one place that wants to
 * leave (a rejected request opening a fresh one) navigates to Initiate, which
 * is a different axis and already travels by `onNavigate`.
 */
export default function RequestsSection({requests, packs, actions, onNavigate, focusFallbackRef, tab, live = false}) {
  const {enqueueSnackbar} = useSnackbar()

  const [ownTab, setOwnTab] = React.useState('approved')
  const [qDraft, setQDraft] = React.useState('')
  const [q, setQ] = React.useState('')
  const [level, setLevel] = React.useState('')

  React.useEffect(() => {
    const timer = setTimeout(() => setQ(qDraft), DEBOUNCE_MS)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDraft])

  // The five stat tiles read the UNFILTERED collection (requestCounts); the
  // three tabs read the filtered one (filterRequests). Two functions, on
  // purpose — see requests/logic.js's own header for why collapsing them
  // into one would let the tiles start following the search box. A driven
  // caller renders the tiles itself and calls the same requestCounts, for the
  // same reason; this stays for the standalone case.
  const counts = React.useMemo(() => requestCounts(requests), [requests])
  const rows = React.useMemo(() => filterRequests(requests, {q, level}, packs), [requests, q, level, packs])

  // The controlled value wins when supplied; otherwise the axis is this
  // component's own, which is the standalone behaviour every other consumer of
  // this section still gets.
  const activeTab = tab || ownTab

  // The section's only write. `preview` is a PURE call into the same
  // applySubmission the store's reducer calls to actually commit the
  // change — computed here only to know what to say in the toast (the
  // round it landed on, whether the budget is now exhausted), never to
  // duplicate the round-advance/cap logic itself. If the budget was
  // already exhausted `preview === req` (a no-op) and nothing is dispatched
  // or toasted — unreachable via the UI today, since the submit button is
  // disabled once `canSubmit(req)` is false, but defended anyway.
  const handleSubmit = React.useCallback((req, payload) => {
    // Live: the server applies the rules and index.js's action toasts on its own reply — a
    // client-side "sent" here would claim success before the backend has agreed.
    if (live) { actions.submitAdditionalData(req.id, payload); return }
    const preview = applySubmission(req, payload, {actor: actorFor(MERCHANT), today: todayISO()})
    if (preview === req) return
    actions.submitAdditionalData(req.id, payload)
    const exhausted = preview.submissions >= MAX_SUBMISSIONS
    enqueueSnackbar(
      exhausted
        ? `Submission ${preview.submissions} of ${MAX_SUBMISSIONS} sent. ${req.id} is now on round ${preview.round}. No further submissions are available.`
        : `Submission ${preview.submissions} of ${MAX_SUBMISSIONS} sent. ${req.id} is now on round ${preview.round}, awaiting a Prozo decision.`,
      {variant: exhausted ? 'warning' : 'success'},
    )
  }, [actions, enqueueSnackbar, live])

  // The merchant's two answers to a Prozo auto-proposal (T12/T13). Same split as handleSubmit:
  // seeded mode transitions locally and says so; live mode lets the API-backed action speak.
  const handleAcceptProposal = React.useCallback((req) => {
    actions.acceptProposal(req.id)
    if (!live) enqueueSnackbar(`${req.id}: proposal accepted — ${req.subject} is now an approved weight freeze at ${req.chargeKg} kg.`, {variant: 'success'})
  }, [actions, enqueueSnackbar, live])

  const handleDeclineProposal = React.useCallback((req, remark) => {
    actions.declineProposal(req.id, remark)
    if (!live) enqueueSnackbar(`${req.id}: proposal declined.`, {variant: 'info'})
  }, [actions, enqueueSnackbar, live])

  const handleFresh = React.useCallback(() => {
    enqueueSnackbar(
      'Blank request opened. Nothing was carried over — choose the level, the weights, the packing material and the evidence again.',
      {variant: 'info'},
    )
    onNavigate('initiate')
  }, [enqueueSnackbar, onNavigate])

  const freeze = (MERCHANT.type && MERCHANT.type.freeze) || 'none'

  // Nobero is a Prozo-initiated merchant, so this branch never renders against
  // the shipped dataset. It is kept because "no freeze" is a real account state
  // the module has to state as a status in its own right (see the note below),
  // and because the merchant type is a per-merchant assignment, not a constant.
  if (freeze === 'none') {
    return (
      <Box sx={{display: 'grid', gap: GAP.lg}}>
        <Box sx={sectionCardSx}>
          <EmptyState
            icon={AssignmentOutlinedIcon}
            title={`${MERCHANT.name} has no weight freeze`}
            body={NO_FREEZE_BODY}
            action={(
              <Box sx={{display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap'}}>
                <Button sx={primaryBtnSx} onClick={() => onNavigate('initiate')}>Initiate a weight freeze</Button>
                <Button sx={secondaryBtnSx} onClick={() => onNavigate('sop')}>Read the procedure</Button>
              </Box>
            )}
          />
        </Box>
        <Box sx={{...alertStripSx(INFO), alignItems: 'flex-start'}}>
          <InfoOutlinedIcon sx={{fontSize: 16, mt: '2px'}} />
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], lineHeight: 1.6, fontWeight: WEIGHT.regular}}>
            <Box component='span' sx={{fontWeight: WEIGHT.bold}}>{NO_FREEZE_NOTE_BOLD}</Box>{NO_FREEZE_NOTE}
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{display: 'grid', gap: GAP.lg}}>
      {/* Heading, blurb and stat tiles are this section's own only while it is
          standalone. A caller that drives the axis renders both of the things
          they duplicate: its tab label already names the view on screen, so a
          heading above it is a second answer to a question already answered,
          and its KPI strip is these same five figures placed where they cover
          all of its tabs instead of only the three status ones. What survives
          is the search/filter card and the rows — the only parts of this
          section nothing above it repeats. */}
      {!tab && (
        <>
          <Box>
            {/* Overridden to SIZE.md — parity with disputes-v2's single in-body
                heading tier (see sdd/type-parity-spec.md); sectionTitleSx's own
                SIZE.xl is one of the two competing heading sizes being removed. */}
            <Typography sx={{...sectionTitleSx, fontSize: SIZE.md}}>Requests and statuses</Typography>
            <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.25, maxWidth: 720}}>
              Every weight freeze request on {MERCHANT.name}, from approval through review to rejection. Freezes
              are {freeze === 'prozo' ? 'raised by Prozo Ops' : 'raised by you and approved by Prozo'}.
            </Typography>
          </Box>

          <RqStats counts={counts} />
        </>
      )}

      <Box sx={{...filterBarSx, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 0}}>
        <TextField
          size='small'
          value={qDraft}
          onChange={(event) => setQDraft(event.target.value)}
          placeholder='Search request, SKU, reason'
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon sx={{fontSize: 16, color: SUBTLE}} />
              </InputAdornment>
            ),
          }}
          inputProps={{'aria-label': 'Search requests'}}
          sx={{fontFamily: FONT, fontSize: SIZE['2xs'], minWidth: 240}}
        />
        <TextField
          select
          size='small'
          value={level}
          onChange={(event) => setLevel(event.target.value)}
          inputProps={{'aria-label': 'Filter by level'}}
          SelectProps={{MenuProps: selectMenuProps}}
          sx={{fontFamily: FONT, fontSize: SIZE['2xs'], minWidth: 180}}
        >
          <MenuItem value=''>All levels</MenuItem>
          {Object.values(FREEZE_LEVEL).map((v) => (
            <MenuItem key={v} value={v}>{FREEZE_LEVEL_LABEL[v] || v}</MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{display: 'grid', gap: GAP.md}}>
        {/* Not rendered when a caller drives the axis: that caller's row already
            selects these three, and a second row of the same three tabs would be
            two controls for one axis, free to disagree.

            MUI's own Tabs gives correct arrow-key wrap-around for free — no
            hand-rolled keyboard handler here. */}
        {!tab && (
          <Tabs
            value={activeTab}
            onChange={(event, next) => setOwnTab(next)}
            sx={{minHeight: 36, '& .MuiTabs-indicator': {bgcolor: PRIMARY_BLUE}}}
          >
            {STATUS_ORDER.map((id) => (
              <Tab
                key={id}
                value={id}
                label={statusTabLabel(id, rows)}
                sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE['2xs'], textTransform: 'none', minHeight: 36}}
              />
            ))}
          </Tabs>
        )}

        {activeTab === 'approved' && (
          <RqApproved rows={rows.approved} packs={packs} onNavigate={onNavigate} focusFallbackRef={focusFallbackRef} />
        )}
        {activeTab === 'review' && (
          <RqReview
            rows={rows.review} packs={packs} onSubmit={handleSubmit} onNavigate={onNavigate}
            onAcceptProposal={handleAcceptProposal} onDeclineProposal={handleDeclineProposal}
            focusFallbackRef={focusFallbackRef}
          />
        )}
        {activeTab === 'rejected' && (
          <RqRejected rows={rows.rejected} packs={packs} onFresh={handleFresh} focusFallbackRef={focusFallbackRef} />
        )}
      </Box>
    </Box>
  )
}
